import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLUMNS, PRIORITIES, LABELS, loadTasks, saveTasks } from './taskData';
import TaskCard from './TaskCard';
import TaskModal from './TaskModal';
import TaskForm from './TaskForm';
import './TaskManager.css';

function TaskManager() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(loadTasks);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterLabel, setFilterLabel] = useState('all');

  const updateAndSave = useCallback((newTasks) => {
    setTasks(newTasks);
    saveTasks(newTasks);
  }, []);

  const getColumnTasks = (columnId) => {
    return tasks
      .filter((t) => t.column === columnId)
      .filter((t) => {
        if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
        if (filterLabel !== 'all' && !t.labels.includes(filterLabel)) return false;
        return true;
      });
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (draggedTask && draggedTask.column !== columnId) {
      const updated = tasks.map((t) =>
        t.id === draggedTask.id ? { ...t, column: columnId } : t
      );
      updateAndSave(updated);
    }
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const handleCreateTask = (taskData) => {
    const newTask = {
      ...taskData,
      id: Date.now().toString(),
      createdAt: Date.now(),
    };
    updateAndSave([...tasks, newTask]);
    setShowForm(false);
  };

  const handleUpdateTask = (taskData) => {
    const updated = tasks.map((t) => (t.id === taskData.id ? { ...t, ...taskData } : t));
    updateAndSave(updated);
    setEditingTask(null);
    setShowForm(false);
    if (selectedTask && selectedTask.id === taskData.id) {
      setSelectedTask({ ...selectedTask, ...taskData });
    }
  };

  const handleDeleteTask = (taskId) => {
    updateAndSave(tasks.filter((t) => t.id !== taskId));
    setSelectedTask(null);
  };

  const handleToggleSubtask = (taskId, subtaskId) => {
    const updated = tasks.map((t) => {
      if (t.id !== taskId) return t;
      return {
        ...t,
        subtasks: t.subtasks.map((s) =>
          s.id === subtaskId ? { ...s, done: !s.done } : s
        ),
      };
    });
    updateAndSave(updated);
    const updatedTask = updated.find((t) => t.id === taskId);
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(updatedTask);
    }
  };

  const handleResetDemo = () => {
    localStorage.removeItem('demohub_tasks');
    setTasks(loadTasks());
  };

  const totalTasks = tasks.length;
  const doneTasks = tasks.filter((t) => t.column === 'done').length;

  return (
    <div className="task-manager">
      <div className="tm-topbar">
        <div className="tm-topbar-left">
          <button className="tm-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="tm-title-group">
            <h1 className="tm-title">Task Manager</h1>
            <span className="tm-task-count">{doneTasks}/{totalTasks} completed</span>
          </div>
        </div>
        <div className="tm-topbar-right">
          <div className="tm-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="tm-filter"
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
          >
            <option value="all">All Priorities</option>
            {Object.entries(PRIORITIES).map(([key, p]) => (
              <option key={key} value={key}>{p.icon} {p.label}</option>
            ))}
          </select>
          <select
            className="tm-filter"
            value={filterLabel}
            onChange={(e) => setFilterLabel(e.target.value)}
          >
            <option value="all">All Labels</option>
            {LABELS.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <button className="tm-reset-btn" onClick={handleResetDemo} title="Reset demo data">
            ↻ Reset
          </button>
          <button className="tm-add-btn" onClick={() => { setEditingTask(null); setShowForm(true); }}>
            + New Task
          </button>
        </div>
      </div>

      <div className="tm-progress-bar">
        <div
          className="tm-progress-fill"
          style={{ width: totalTasks ? `${(doneTasks / totalTasks) * 100}%` : '0%' }}
        ></div>
      </div>

      <div className="tm-board">
        {Object.values(COLUMNS).map((col) => {
          const colTasks = getColumnTasks(col.id);
          return (
            <div
              key={col.id}
              className={`tm-column ${dragOverColumn === col.id ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={() => setDragOverColumn(null)}
              onDrop={(e) => handleDrop(e, col.id)}
            >
              <div className="tm-column-header">
                <div className="tm-column-title">
                  <span className="tm-column-icon">{col.icon}</span>
                  <span>{col.title}</span>
                  <span className="tm-column-count">{colTasks.length}</span>
                </div>
              </div>
              <div className="tm-column-body">
                {colTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedTask(task)}
                  />
                ))}
                {colTasks.length === 0 && (
                  <div className="tm-column-empty">
                    {dragOverColumn === col.id ? 'Drop here' : 'No tasks'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onEdit={(task) => { setEditingTask(task); setShowForm(true); setSelectedTask(null); }}
          onDelete={handleDeleteTask}
          onToggleSubtask={handleToggleSubtask}
        />
      )}

      {showForm && (
        <TaskForm
          task={editingTask}
          onSave={editingTask ? handleUpdateTask : handleCreateTask}
          onClose={() => { setShowForm(false); setEditingTask(null); }}
        />
      )}
    </div>
  );
}

export default TaskManager;
